// apps/mtd-itsa-web/src/providers/project-provider.tsx
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getSupabaseClient } from '@kordbooks/supabase-client';
import { useAuth } from './auth-provider';

export interface Project {
    id: string;
    name: string;
    type?: string | null;
    accountancy_id?: string | null;
}

interface ProjectContextType {
    currentProjectId: string | null;
    projects: Project[];
    setCurrentProjectId: (projectId: string) => void;
    loading: boolean;
    error: string | null;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function ProjectProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const [currentProjectId, setCurrentProjectIdState] = useState<string | null>(null);
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchUserProjects = async () => {
            if (!user) {
                setProjects([]);
                setCurrentProjectIdState(null);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const supabase = getSupabaseClient();

                // Step 1: Fetch projects by joining the new auth_project_members table
                const { data: membersData, error: membersError } = await supabase
                    .from('auth_project_members')
                    .select(`
                        project:auth_projects (
                            project_id,
                            project_name,
                            type,
                            accountancy_id
                        )
                    `)
                    .eq('user_uuid', user.id);

                if (membersError) {
                    throw membersError;
                }

                // Map to Project interface with explicit return type
                const fetchedProjects: Project[] = (membersData || [])
                    .map((item: any): Project | null => {
                        const p = item.project;
                        // Handle potential null project (though rare with inner join logic)
                        if (!p) return null;

                        return {
                            id: p.project_id,
                            name: p.project_name || `Project ${p.project_id.slice(0, 8)}`,
                            type: p.type,
                            accountancy_id: p.accountancy_id,
                        };
                    })
                    .filter((p): p is Project => p !== null);

                setProjects(fetchedProjects);

                // Set default to first project or restore from localStorage
                const savedProjectId = localStorage.getItem('current-project-id');
                if (savedProjectId && fetchedProjects.some((p) => p.id === savedProjectId)) {
                    setCurrentProjectIdState(savedProjectId);
                } else if (fetchedProjects.length > 0) {
                    // Default to first project in array
                    setCurrentProjectIdState(fetchedProjects[0].id);
                } else {
                    setCurrentProjectIdState(null);
                }
            } catch (err) {
                console.error('Error fetching user projects:', err);
                setError('Failed to load projects');
                setProjects([]);
                setCurrentProjectIdState(null);
            } finally {
                setLoading(false);
            }
        };

        fetchUserProjects();
    }, [user]);

    const setCurrentProjectId = (projectId: string) => {
        if (projects.some((p) => p.id === projectId)) {
            setCurrentProjectIdState(projectId);
            localStorage.setItem('current-project-id', projectId);
        }
    };

    return (
        <ProjectContext.Provider
            value={{
                currentProjectId,
                projects,
                setCurrentProjectId,
                loading,
                error,
            }}
        >
            {children}
        </ProjectContext.Provider>
    );
}

export function useProject() {
    const context = useContext(ProjectContext);
    if (!context) {
        throw new Error('useProject must be used within a ProjectProvider');
    }
    return context;
}
